import {TreeNode} from 'primeng/api';
import {CpmCLocationApiModel} from '../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-location.api-model';
import {ECpmCLocationTypeApiEnum} from '../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-location.api-enums';
import {CpmCLocationTreeApiView} from '../../../gen/cpm-c-api-ts/src/views/c-customer/cpmc-location-tree.api-view';

/**
 * Location utils
 */
export abstract class LocationUtils {
    /**
     * Get icon for a given location type
     * @param locationType In location type
     * @returns Icon classes or undefined
     */
    public static getIconForType(locationType?: ECpmCLocationTypeApiEnum): string | undefined {
        switch (locationType) {
            case ECpmCLocationTypeApiEnum.REGION:
                return 'pi pi-globe';

            case ECpmCLocationTypeApiEnum.SITE:
                return 'pi pi-map-marker';

            case ECpmCLocationTypeApiEnum.BUILDING:
                return 'pi pi-building';

            case ECpmCLocationTypeApiEnum.FLOOR:
                return 'pi pi-stop';

            case ECpmCLocationTypeApiEnum.ROOM:
                return 'pi pi-sign-in';

            case ECpmCLocationTypeApiEnum.RACK:
                return 'pi pi-server';
        }

        return undefined;
    }
    /**
     * Build location hierarchy tree
     * @param locations Location array
     * @param rootNode Root node
     * @param autoExpand Location type to auto expand
     * @param selectables Selectable location types
     */
    public static buildLocationHierarchyTree(
        locations: (CpmCLocationApiModel | CpmCLocationTreeApiView)[],
        rootNode: TreeNode<CpmCLocationApiModel | CpmCLocationTreeApiView>,
        autoExpand: ECpmCLocationTypeApiEnum[],
        selectables: ECpmCLocationTypeApiEnum[]
    ) {
        const locByUUID: Record<string, TreeNode<CpmCLocationApiModel | CpmCLocationTreeApiView>> = {};

        const remaining = locations.map((x) => x);
        while (remaining.length > 0) {
            for (let i = 0; i < remaining.length; ++i) {
                const current = remaining[i];

                // Child location
                if (current.parent_uuid && current.parent_uuid in locByUUID) {
                    const newNode: TreeNode<CpmCLocationApiModel | CpmCLocationTreeApiView> = {
                        key: current.uuid!,
                        label: current.name,
                        data: current,
                        icon: LocationUtils.getIconForType(current.type),
                        expanded: current.type && autoExpand.indexOf(current.type) != -1,
                        selectable: current.type && selectables.indexOf(current.type) != -1,
                        parent: locByUUID[current.parent_uuid],
                        children: []
                    };

                    locByUUID[current.parent_uuid].children!.push(newNode);
                    locByUUID[current.uuid!] = newNode;

                    remaining.splice(i, 1);
                    i--;
                }
                // Root location
                else if (!current.parent_uuid) {
                    const newNode: TreeNode<CpmCLocationApiModel | CpmCLocationTreeApiView> = {
                        key: current.uuid!,
                        label: current.name,
                        data: current,
                        icon: LocationUtils.getIconForType(current.type),
                        expanded: current.type && autoExpand.indexOf(current.type) != -1,
                        selectable: current.type && selectables.indexOf(current.type) != -1,
                        parent: rootNode,
                        children: []
                    };

                    rootNode.children!.push(newNode);
                    locByUUID[current.uuid!] = newNode;

                    remaining.splice(i, 1);
                    i--;
                }
            }
        }
    }

    /**
     * Check if a location is a global location (root location without parent)
     * @param location Location to check
     * @returns True if the location is defined and has no parent_uuid, false otherwise
     */
    public static isGlobalLocation(location?: CpmCLocationApiModel): boolean {
        return location !== undefined && location.parent_uuid === undefined;
    }
}
